import type { MediaCrop } from '@/types/media';

import type { Node, Edge, NodeProps as ReactFlowNodeProps, EdgeProps as ReactFlowEdgeProps } from '@xyflow/react';

// ========== NODE DATA TYPE ==========

export interface FlowNodeData extends Record<string, unknown> {
    id: number;
    node_type: string;
    node_name: string;
    config: NodeConfig;
    buttons?: ButtonData[];
    onEdit?: (nodeId: number) => void;
    onDelete?: (nodeId: number) => void;
    onDuplicate?: (nodeId: number) => void;
    isSelected?: boolean;
    tag_nome?: string;
    tag_cor?: string;
    is_random?: boolean;
    is_sequence?: boolean;
    canDelete?: boolean;
}

export interface UrlEntry {
    url: string;
    weight: number; // peso percentual (0-100)
}

export interface BlockButton {
    /** Plataforma v2: id do botão (a porta de um botão de ação sai dele). */
    id?: string;
    text: string;
    type: 'url' | 'postback';
    value: string;             // usado apenas para postback
    url_entries?: UrlEntry[];  // múltiplas URLs ponderadas (apenas type='url')
}

export interface ContentBlock {
    type: 'text' | 'image' | 'video' | 'audio';
    message?: string;
    title?: string;
    subtitle?: string;
    media_url?: string;
    media_filename?: string;
    attachment_id?: string;
    default_action_url?: string;
    ai_translate?: boolean;
    buttons?: BlockButton[];
}

export interface NodeConfig {
    message?: string;
    title?: string;
    subtitle?: string;
    media_url?: string;
    media_type?: string;
    media_filename?: string;
    // media_url e a imagem JA recortada (a que vai para a Meta). Estes dois
    // guardam de onde ela saiu e como foi enquadrada, para reabrir o editor.
    media_source_url?: string | null;
    media_crop?: MediaCrop | null;
    delay_seconds?: number;
    triggers?: TriggerItem[];
    buttons?: ButtonData[];
    tag_id?: number;
    tag_name?: string;
    attachment_id?: string;
    goto_funnel_id?: number;
    goto_trigger_node_id?: number;
    goto_funnel_name?: string;
    blocks?: ContentBlock[];
    [key: string]: unknown;
}

export interface ButtonData {
    id: number;
    button_text: string;
    button_type?: 'postback' | 'url';
    button_value?: string;
    button_order?: number;
    position?: number;
    url?: string;
}

export interface TriggerItem {
    trigger_type: 'keyword' | 'tag' | 'exact_match';
    trigger_value: string;
}

// ========== REACT FLOW TYPES ==========

export type FlowNode = Node<FlowNodeData, string>;

export interface FlowEdgeData extends Record<string, unknown> {
    id?: number;
    button_id?: number;
    buttonText?: string;
    condition_type?: string;
    condition_value?: string;
    onDelete?: (transitionId: number) => void;
}

export type FlowEdge = Edge<FlowEdgeData, string>;

// ========== CUSTOM PROPS ==========

export type FlowNodeProps = ReactFlowNodeProps<FlowNode>;

export type FlowEdgeProps = ReactFlowEdgeProps<FlowEdge>;

// ========== FLOW STATE ==========

export interface FlowState {
    nodes: FlowNode[];
    edges: FlowEdge[];
    selectedNode: FlowNode | null;
    isLoading: boolean;
    isSaving: boolean;
}

// ========== API RESPONSE TYPE ==========

export interface NodeResponseFromAPI {
    id: number;
    funnel_id: number;
    node_type_id: number;
    nome: string | null;
    position_x: number;
    position_y: number;
    message: string | null;
    title: string | null;
    subtitle: string | null;
    media_url: string | null;
    media_type: string | null;
    media_filename: string | null;
    media_source_url?: string | null;
    media_crop?: MediaCrop | string | null;
    is_trigger: number;
    is_random: number | null;
    is_sequence?: number | null;
    delay_seconds: number | null;
    created_at: string;
    updated_at: string;
    type_name: string;
    triggers: TriggerItem[];
    buttons: ButtonData[];
    tag_id?: number | null;
    tag_name?: string | null;
    tag_nome?: string;
    tag_cor?: string;
    goto_funnel_id?: number | null;
    goto_trigger_node_id?: number | null;
    goto_funnel_name?: string | null;
    blocks?: string | null;
}

export interface TransitionResponseFromAPI {
    id: number;
    from_node_id: number;
    to_node_id: number;
    to_button_id: number | null;
    created_at: string;
    updated_at: string;
}

// ========== TRANSFORMERS ==========

export const transformNodeToFlowNode = (node: NodeResponseFromAPI): FlowNode => {
    // Parse blocks JSON string from API
    let parsedBlocks: ContentBlock[] | undefined;
    if (node.blocks) {
        try {
            parsedBlocks = typeof node.blocks === 'string' ? JSON.parse(node.blocks) : node.blocks;
        } catch {
            parsedBlocks = undefined;
        }
    }

    let parsedCrop: MediaCrop | undefined;
    if (node.media_crop) {
        try {
            parsedCrop = typeof node.media_crop === 'string'
                ? JSON.parse(node.media_crop)
                : node.media_crop;
        } catch {
            parsedCrop = undefined;
        }
    }

    const config: NodeConfig = {
        message: node.message ?? undefined,
        title: node.title ?? undefined,
        subtitle: node.subtitle ?? undefined,
        media_url: node.media_url ?? undefined,
        media_type: node.media_type ?? undefined,
        media_filename: node.media_filename ?? undefined,
        media_source_url: node.media_source_url ?? undefined,
        // Coluna JSON: dependendo do driver chega como objeto ou como string.
        media_crop: parsedCrop,
        delay_seconds: node.delay_seconds ?? undefined,
        triggers: node.triggers || [],
        buttons: node.buttons || [],
        tag_id: node.tag_id ?? undefined,
        tag_name: node.tag_name ?? undefined,
        goto_funnel_id: node.goto_funnel_id ?? undefined,
        goto_trigger_node_id: node.goto_trigger_node_id ?? undefined,
        goto_funnel_name: node.goto_funnel_name ?? undefined,
        blocks: parsedBlocks,
    };

    const defaultNames: Record<string, string> = {
        trigger: 'Gatilho',
        message: 'Mensagem',
        message_template: 'Template de Mensagem',
        photo: 'Foto',
        photo_template: 'Template de Foto',
        video: 'Vídeo',
        video_template: 'Template de Vídeo',
        audio: 'Áudio',
        delay: 'Aguardar',
        tag: 'Tag',
        go_to: 'Go To',
        reply: 'Resposta Pública',
    };

    return {
        id: String(node.id),
        type: node.type_name,
        position: { x: node.position_x, y: node.position_y },
        data: {
            id: node.id,
            node_type: node.type_name,
            node_name: node.nome || defaultNames[node.type_name] || 'Node',
            config: config,
            buttons: node.buttons || [],
            tag_nome: node.tag_nome,
            tag_cor: node.tag_cor,
            is_random: !!node.is_random,
            is_sequence: !!node.is_sequence,
        },
    };
};

export const transformTransitionToFlowEdge = (
    transition: TransitionResponseFromAPI,
    nodes: NodeResponseFromAPI[]
): FlowEdge => {
    let buttonText: string | undefined;
    let sourceHandle = 'source';

    if (transition.to_button_id) {
        sourceHandle = `button-${transition.to_button_id}`;
        const sourceNode = nodes.find(n => n.id === transition.from_node_id);
        if (sourceNode?.buttons) {
            const button = sourceNode.buttons.find((b) => b.id === transition.to_button_id);
            if (button) {
                buttonText = button.button_text;
            }
        }
    }

    return {
        id: String(transition.id),
        source: String(transition.from_node_id),
        target: String(transition.to_node_id),
        sourceHandle: sourceHandle,
        type: 'custom',
        animated: true,
        data: {
            id: transition.id,
            button_id: transition.to_button_id ?? undefined,
            buttonText: buttonText,
        },
    };
};